import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, parseISO, isValid } from 'date-fns';
import { api } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Button, Card, Badge, cn } from '../../components/ui/Kit';
import EmptyState from '../../components/ui/EmptyState';
import Spinner from '../../components/ui/Spinner';
import Pagination from '../../components/ui/Pagination';
import SortableHeader from '../../components/ui/SortableHeader';
import Modal from '../../components/ui/Modal';
import { useToast } from '../../components/ui/Toast';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {
  FiPlus, FiEdit2, FiArchive, FiTrash2, FiStar, FiLayout, FiList,
  FiBookOpen, FiLock,
} = FiIcons;

const PAGE_SIZE = 25;
const ADMIN_ROLES = new Set(['admin', 'super_admin']);
const STATUS_COLUMNS = ['researching', 'testing', 'active', 'archived'];

const RESOURCE_TYPE_OPTIONS = [
  { label: 'Ad', value: 'ad' },
  { label: 'Funnel', value: 'funnel' },
  { label: 'Video', value: 'video' },
  { label: 'Competitor', value: 'competitor' },
  { label: 'Swipe', value: 'swipe' },
  { label: 'Tool', value: 'tool' },
  { label: 'Note', value: 'note' },
];

const PLATFORM_OPTIONS = [
  { label: 'Facebook', value: 'facebook' },
  { label: 'Instagram', value: 'instagram' },
  { label: 'YouTube', value: 'youtube' },
  { label: 'LinkedIn', value: 'linkedin' },
  { label: 'Other', value: 'other' },
];

const CATEGORY_OPTIONS = [
  { label: 'Recruiting', value: 'recruiting' },
  { label: 'Life Insurance', value: 'life_insurance' },
  { label: 'Both', value: 'both' },
];

const TRAFFIC_OPTIONS = [
  { label: 'Organic', value: 'organic' },
  { label: 'Paid', value: 'paid' },
  { label: 'Both', value: 'both' },
];

const STATUS_OPTIONS = [
  { label: 'Researching', value: 'researching' },
  { label: 'Testing', value: 'testing' },
  { label: 'Active', value: 'active' },
  { label: 'Archived', value: 'archived' },
];

const EMPTY_FILTERS = {
  resource_type: '',
  platform: '',
  category: '',
  traffic_type: '',
  status: '',
};

const RESOURCE_TYPE_BADGE = {
  ad: 'bg-blue-100 text-blue-800',
  funnel: 'bg-purple-100 text-purple-800',
  video: 'bg-red-100 text-red-800',
  competitor: 'bg-amber-100 text-amber-800',
  swipe: 'bg-green-100 text-green-800',
  tool: 'bg-gray-100 text-gray-800',
  note: 'bg-slate-100 text-slate-800',
};

const STATUS_LABELS = {
  researching: 'Researching',
  testing: 'Testing',
  active: 'Active',
  archived: 'Archived',
};

function formatLabel(value) {
  if (!value) return '—';
  return value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatCapturedDate(value) {
  if (!value) return '—';
  const d = typeof value === 'string' ? parseISO(value) : new Date(value);
  return isValid(d) ? format(d, 'MMM d, yyyy') : '—';
}

function ResourceTypeBadge({ type }) {
  if (!type) return null;
  return (
    <span className={cn('inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold', RESOURCE_TYPE_BADGE[type] || 'bg-gray-100 text-gray-800')}>
      {formatLabel(type)}
    </span>
  );
}

function StarRating({ value }) {
  return (
    <span className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => {
        const filled = value != null && n <= value;
        return (
          <SafeIcon
            key={n}
            icon={FiStar}
            className={cn('w-3.5 h-3.5', filled ? 'text-amber-400 fill-amber-400' : 'text-gray-200 fill-gray-200')}
          />
        );
      })}
    </span>
  );
}

function ResourceCard({ resource, onEdit }) {
  return (
    <Card className="p-3 hover:ring-2 ring-primary/20 transition-all">
      <div className="flex items-start justify-between gap-2 mb-2">
        <p className="font-semibold text-sm text-gray-900 line-clamp-2 flex-1">{resource.title}</p>
        <button
          type="button"
          onClick={() => onEdit(resource)}
          className="flex-shrink-0 p-1 rounded text-gray-400 hover:text-primary hover:bg-gray-100"
          title="Edit"
        >
          <SafeIcon icon={FiEdit2} className="w-3.5 h-3.5" />
        </button>
      </div>
      {resource.author_name && (
        <p className="text-xs text-gray-500 mb-1.5 truncate">{resource.author_name}</p>
      )}
      <div className="flex flex-wrap items-center gap-1.5 mb-2">
        <ResourceTypeBadge type={resource.resource_type} />
        {resource.platform && (
          <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium bg-gray-100 text-gray-700">
            {formatLabel(resource.platform)}
          </span>
        )}
      </div>
      <div className="mb-2">
        <StarRating value={resource.credibility} />
      </div>
      {resource.notes && (
        <p className="text-xs text-gray-500 line-clamp-2 mb-2">{resource.notes}</p>
      )}
      {resource.captured_at && (
        <p className="text-[10px] text-gray-400">{formatCapturedDate(resource.captured_at)}</p>
      )}
    </Card>
  );
}

function FilterSelect({ label, value, onChange, options }) {
  return (
    <div className="min-w-[140px]">
      <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-9 w-full rounded-md border border-gray-300 bg-white px-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
      >
        <option value="">All</option>
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    </div>
  );
}

export default function MarketingIntelligence() {
  const { user } = useAuth();
  const { addToast } = useToast();
  const navigate = useNavigate();

  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('board');
  const [filters, setFilters] = useState({ ...EMPTY_FILTERS });
  const [sortField, setSortField] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(1);

  const [archiveTarget, setArchiveTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const isAdmin = ADMIN_ROLES.has(user?.role);
  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  const loadResources = useCallback(async () => {
    if (!user?.agency_id) return;
    setLoading(true);
    try {
      const apiFilters = {};
      if (filters.resource_type) apiFilters.resource_type = filters.resource_type;
      if (filters.platform) apiFilters.platform = filters.platform;
      if (filters.category) apiFilters.category = filters.category;
      if (filters.traffic_type) apiFilters.traffic_type = filters.traffic_type;
      if (filters.status) apiFilters.status = filters.status;
      const data = await api.marketingIntelligence.getAll(user.agency_id, apiFilters);
      setResources(data ?? []);
    } catch (err) {
      addToast({ type: 'error', title: 'Failed to load resources', message: err.message });
    } finally {
      setLoading(false);
    }
  }, [user?.agency_id, filters, addToast]);

  useEffect(() => {
    if (!isAdmin) { setLoading(false); return; }
    if (user?.agency_id) loadResources();
  }, [isAdmin, user?.agency_id, loadResources]);

  useEffect(() => { setPage(1); }, [filters, sortField, sortDir, viewMode]);

  const clearFilters = () => setFilters({ ...EMPTY_FILTERS });

  const handleArchiveConfirm = async () => {
    if (!archiveTarget) return;
    setActionLoading(true);
    try {
      await api.marketingIntelligence.archive(archiveTarget.id);
      addToast({ type: 'success', title: 'Resource archived' });
      setArchiveTarget(null);
      await loadResources();
    } catch (err) {
      addToast({ type: 'error', title: 'Archive failed', message: err.message });
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setActionLoading(true);
    try {
      await api.marketingIntelligence.delete(deleteTarget.id);
      addToast({ type: 'success', title: 'Resource deleted' });
      setDeleteTarget(null);
      await loadResources();
    } catch (err) {
      addToast({ type: 'error', title: 'Delete failed', message: err.message });
    } finally {
      setActionLoading(false);
    }
  };

  const handleTableArchive = async (resource) => {
    setActionLoading(true);
    try {
      await api.marketingIntelligence.archive(resource.id);
      addToast({ type: 'success', title: 'Resource archived' });
      await loadResources();
    } catch (err) {
      addToast({ type: 'error', title: 'Archive failed', message: err.message });
    } finally {
      setActionLoading(false);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortField(field); setSortDir('asc'); }
  };

  const sortedResources = useMemo(() => {
    const list = [...resources];
    list.sort((a, b) => {
      let aVal = a[sortField] ?? '';
      let bVal = b[sortField] ?? '';
      if (sortField === 'title') { aVal = String(aVal).toLowerCase(); bVal = String(bVal).toLowerCase(); }
      if (sortField === 'captured_at' || sortField === 'created_at') {
        const aDate = aVal ? new Date(aVal) : new Date(0);
        const bDate = bVal ? new Date(bVal) : new Date(0);
        return sortDir === 'asc' ? aDate - bDate : bDate - aDate;
      }
      if (sortField === 'credibility') { aVal = a.credibility ?? -1; bVal = b.credibility ?? -1; }
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return list;
  }, [resources, sortField, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sortedResources.length / PAGE_SIZE));
  const paginated = sortedResources.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const sortProps = { sortField, sortDir, onSort: handleSort };

  const resourcesByStatus = useMemo(() => {
    const map = Object.fromEntries(STATUS_COLUMNS.map((s) => [s, []]));
    resources.forEach((r) => { if (map[r.status]) map[r.status].push(r); });
    return map;
  }, [resources]);

  if (!isAdmin) {
    return (
      <EmptyState
        icon={FiLock}
        title="Admin access required"
        description="Marketing Intelligence is available to admins only."
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Marketing Intelligence</h1>
          <p className="text-sm text-gray-500">Your agency&apos;s competitive research library.</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex bg-gray-200 p-1 rounded-lg">
            <button
              type="button"
              onClick={() => setViewMode('board')}
              className={cn(
                'px-3 py-1.5 text-sm font-medium rounded-md transition-colors inline-flex items-center gap-1.5',
                viewMode === 'board' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900',
              )}
            >
              <SafeIcon icon={FiLayout} className="w-4 h-4" />
              Board View
            </button>
            <button
              type="button"
              onClick={() => setViewMode('table')}
              className={cn(
                'px-3 py-1.5 text-sm font-medium rounded-md transition-colors inline-flex items-center gap-1.5',
                viewMode === 'table' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900',
              )}
            >
              <SafeIcon icon={FiList} className="w-4 h-4" />
              Table View
            </button>
          </div>
          <Button onClick={() => navigate('/marketing-intelligence/new')}>
            <SafeIcon icon={FiPlus} className="mr-2" />
            Add Resource
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <FilterSelect label="Resource Type" value={filters.resource_type} onChange={(v) => setFilters((f) => ({ ...f, resource_type: v }))} options={RESOURCE_TYPE_OPTIONS} />
        <FilterSelect label="Platform" value={filters.platform} onChange={(v) => setFilters((f) => ({ ...f, platform: v }))} options={PLATFORM_OPTIONS} />
        <FilterSelect label="Category" value={filters.category} onChange={(v) => setFilters((f) => ({ ...f, category: v }))} options={CATEGORY_OPTIONS} />
        <FilterSelect label="Traffic Type" value={filters.traffic_type} onChange={(v) => setFilters((f) => ({ ...f, traffic_type: v }))} options={TRAFFIC_OPTIONS} />
        <FilterSelect label="Status" value={filters.status} onChange={(v) => setFilters((f) => ({ ...f, status: v }))} options={STATUS_OPTIONS} />
        {activeFilterCount > 0 && (
          <button type="button" onClick={clearFilters} className="text-sm text-primary hover:text-primary-dark font-medium pb-1">
            Clear Filters
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center py-16 gap-3">
          <Spinner size="lg" className="text-primary" />
          <p className="text-sm text-gray-500">Loading resources…</p>
        </div>
      ) : resources.length === 0 ? (
        <EmptyState
          icon={FiBookOpen}
          title={activeFilterCount > 0 ? 'No results found' : 'No resources yet'}
          description={
            activeFilterCount > 0
              ? 'Try adjusting your filters.'
              : 'Start building your research library by adding your first resource.'
          }
          actionLabel={activeFilterCount > 0 ? 'Clear Filters' : 'Add Resource'}
          onAction={
            activeFilterCount > 0
              ? clearFilters
              : () => navigate('/marketing-intelligence/new')
          }
        />
      ) : viewMode === 'board' ? (
        <div className="overflow-x-auto pb-4">
          <div className="flex gap-4 min-h-[560px]">
            {STATUS_COLUMNS.map((status) => (
              <div
                key={status}
                className="w-72 flex-shrink-0 flex flex-col rounded-lg border border-gray-200 bg-gray-100"
              >
                <div className="p-3 border-b border-gray-200 flex justify-between items-center bg-gray-50 rounded-t-lg">
                  <h3 className="font-semibold text-gray-800 text-sm">{STATUS_LABELS[status]}</h3>
                  <Badge variant="default">{resourcesByStatus[status].length}</Badge>
                </div>
                <div className="flex-1 p-2 space-y-2 overflow-y-auto min-h-[120px] max-h-[calc(100vh-280px)]">
                  {resourcesByStatus[status].length === 0 ? (
                    <p className="text-xs text-gray-400 text-center py-6">No resources</p>
                  ) : (
                    resourcesByStatus[status].map((resource) => (
                      <ResourceCard
                        key={resource.id}
                        resource={resource}
                        onEdit={(r) => navigate(`/marketing-intelligence/${r.id}/edit`)}
                      />
                    ))
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <SortableHeader label="Title" field="title" {...sortProps} className="px-6 py-3" />
                  <SortableHeader label="Author" field="author_name" {...sortProps} className="px-6 py-3" />
                  <SortableHeader label="Type" field="resource_type" {...sortProps} className="px-6 py-3" />
                  <SortableHeader label="Category" field="category" {...sortProps} className="px-6 py-3" />
                  <SortableHeader label="Traffic" field="traffic_type" {...sortProps} className="px-6 py-3" />
                  <SortableHeader label="Status" field="status" {...sortProps} className="px-6 py-3" />
                  <SortableHeader label="Credibility" field="credibility" {...sortProps} className="px-6 py-3" />
                  <SortableHeader label="Captured Date" field="captured_at" {...sortProps} className="px-6 py-3" />
                  <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wide text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {paginated.map((resource) => (
                  <tr key={resource.id} className="hover:bg-gray-50/80">
                    <td className="px-6 py-4">
                      <button
                        type="button"
                        onClick={() => navigate(`/marketing-intelligence/${resource.id}/edit`)}
                        className="font-medium text-primary hover:text-primary-dark hover:underline text-left"
                      >
                        {resource.title}
                      </button>
                    </td>
                    <td className="px-6 py-4 text-gray-600">{resource.author_name || '—'}</td>
                    <td className="px-6 py-4"><ResourceTypeBadge type={resource.resource_type} /></td>
                    <td className="px-6 py-4 text-gray-600">{formatLabel(resource.category)}</td>
                    <td className="px-6 py-4 text-gray-600">{formatLabel(resource.traffic_type)}</td>
                    <td className="px-6 py-4">
                      <Badge variant={resource.status === 'active' ? 'success' : 'default'}>
                        {STATUS_LABELS[resource.status] || formatLabel(resource.status)}
                      </Badge>
                    </td>
                    <td className="px-6 py-4"><StarRating value={resource.credibility} /></td>
                    <td className="px-6 py-4 text-gray-600">{formatCapturedDate(resource.captured_at)}</td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          type="button"
                          title="Edit"
                          onClick={() => navigate(`/marketing-intelligence/${resource.id}/edit`)}
                          className="p-1.5 rounded-md text-gray-400 hover:text-primary hover:bg-gray-100"
                        >
                          <SafeIcon icon={FiEdit2} className="w-4 h-4" />
                        </button>
                        {resource.status !== 'archived' && (
                          <button
                            type="button"
                            title="Archive"
                            disabled={actionLoading}
                            onClick={() => setArchiveTarget(resource)}
                            className="p-1.5 rounded-md text-gray-400 hover:text-amber-600 hover:bg-amber-50"
                          >
                            <SafeIcon icon={FiArchive} className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          type="button"
                          title="Delete"
                          disabled={actionLoading}
                          onClick={() => setDeleteTarget(resource)}
                          className="p-1.5 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50"
                        >
                          <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {sortedResources.length > PAGE_SIZE && (
            <Pagination
              page={page}
              totalPages={totalPages}
              totalItems={sortedResources.length}
              pageSize={PAGE_SIZE}
              onPageChange={setPage}
            />
          )}
        </Card>
      )}

      <Modal isOpen={Boolean(archiveTarget)} onClose={() => setArchiveTarget(null)} title="Archive resource?">
        <p className="text-sm text-gray-600 mt-2">
          Archive <span className="font-medium text-gray-900">{archiveTarget?.title}</span>? It will move to the Archived column.
        </p>
        <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-100">
          <Button variant="outline" size="sm" onClick={() => setArchiveTarget(null)} disabled={actionLoading}>Cancel</Button>
          <Button variant="danger" size="sm" onClick={handleArchiveConfirm} isLoading={actionLoading}>Yes, Archive</Button>
        </div>
      </Modal>

      <Modal isOpen={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} title="Delete resource?">
        <p className="text-sm text-gray-600 mt-2">
          Permanently delete <span className="font-medium text-gray-900">{deleteTarget?.title}</span>? This cannot be undone.
        </p>
        <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-100">
          <Button variant="outline" size="sm" onClick={() => setDeleteTarget(null)} disabled={actionLoading}>Cancel</Button>
          <Button variant="danger" size="sm" onClick={handleDeleteConfirm} isLoading={actionLoading}>Yes, Delete</Button>
        </div>
      </Modal>
    </div>
  );
}
